import { useEffect, useMemo, useState } from 'react';
import ModuleNotEnabledState from '../../../shared/components/ModuleNotEnabledState';
import MembershipCustomerSummaryCard from '../components/MembershipCustomerSummaryCard';
import PointsBalanceCard from '../components/PointsBalanceCard';
import PointsLedgerTable from '../components/PointsLedgerTable';
import ManualPointsAdjustmentDrawer from '../components/ManualPointsAdjustmentDrawer';
import EarnPointsDrawer from '../components/EarnPointsDrawer';
import RedeemPointsDrawer from '../components/RedeemPointsDrawer';
import RevokeMembershipModal from '../components/RevokeMembershipModal';
import RenewMembershipModal from '../components/RenewMembershipModal';
import { useMembership, useMembershipLedger, useMembershipType } from '../queries';
import { toLocalDayEndInstant, toLocalDayStartInstant } from '../utils/ledgerDateRange';
import { earliestMerchantPastDateIso } from '../../../shared/utils/merchantHistoryWindow';

type MembershipCustomerDetailPageProps = {
  membershipEnabled: boolean;
  membershipUuid: string;
  onNavigate?: (path: string) => void;
};

const EVENT_TYPES = ['', 'EARN', 'REDEEM', 'ADJUST_CREDIT', 'ADJUST_DEBIT', 'EXPIRE'] as const;
const EVENT_LABELS: Record<string, string> = { '': 'All', EARN: 'Earn', REDEEM: 'Redeem', ADJUST_CREDIT: 'Credit', ADJUST_DEBIT: 'Debit', EXPIRE: 'Expire' };

const getInitials = (value: string): string => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'C';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const MembershipCustomerDetailPage = ({
  membershipEnabled,
  membershipUuid,
  onNavigate
}: MembershipCustomerDetailPageProps) => {
  const [eventType, setEventType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Merchants may inspect ledger history back at most 6 months.
  const minSelectableDate = useMemo(() => earliestMerchantPastDateIso(), []);
  const [page, setPage] = useState(0);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);

  const membershipQuery = useMembership(membershipUuid, membershipEnabled);
  const membershipTypeQuery = useMembershipType(membershipQuery.data?.membershipTypeUuid, membershipEnabled && Boolean(membershipQuery.data?.membershipTypeUuid));
  const ledgerQuery = useMembershipLedger({
    membershipUuid,
    eventType,
    fromInstant: toLocalDayStartInstant(fromDate),
    toInstant: toLocalDayEndInstant(toDate),
    page,
    pageSize: 20,
    enabled: membershipEnabled
  });

  useEffect(() => {
    setPage(0);
  }, [eventType, fromDate, toDate]);

  const customerName = membershipQuery.data?.customerName || membershipQuery.data?.customerPhone || 'Customer';
  const entryCount = ledgerQuery.data?.items?.length || 0;
  const benefitType = String(membershipQuery.data?.benefitType || membershipTypeQuery.data?.benefitType || '').toUpperCase();
  const isVisitMembership = benefitType === 'VISITS';

  const dateRangeSummary = useMemo(() => {
    const parts: string[] = [];
    if (fromDate) parts.push(`from ${new Date(fromDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}`);
    if (toDate) parts.push(`to ${new Date(toDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}`);
    if (!parts.length) return '';
    return parts.join(' ');
  }, [fromDate, toDate]);

  if (!membershipEnabled) {
    return (
      <ModuleNotEnabledState
        title="Memberships are not enabled"
        description="Upgrade your plan to inspect member balances, ledger activity, and loyalty history."
      />
    );
  }

  return (
    <section className="appointments-v2-page">
      <section className="dashboard-tab-hero">
        <div className="dashboard-tab-hero-copy">
          <p className="dashboard-tab-hero-eyebrow">
            <button type="button" className="btn ghost small" onClick={() => onNavigate?.('/store/memberships')} style={{ padding: 0, fontSize: 'inherit', color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              Memberships
            </button>
            {' › '}
            {customerName}
          </p>
          <div className="membership-customer-hero-row">
            <span className="membership-customer-hero-avatar">{getInitials(customerName)}</span>
            <div>
              <h3 className="dashboard-tab-hero-title">{customerName}'s Membership</h3>
              <p className="dashboard-tab-hero-subtitle">Inspect membership health and points activity.</p>
            </div>
          </div>
        </div>
        <div className="membership-customer-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={() => setRenewOpen(true)}
            disabled={!membershipQuery.data?.membershipUuid}
          >
            Renew
          </button>
          <button
            type="button"
            className="btn ghost danger"
            onClick={() => setRevokeOpen(true)}
            disabled={!membershipQuery.data?.membershipUuid}
          >
            Revoke
          </button>
          {!isVisitMembership ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setEarnOpen(true)}
              disabled={!membershipQuery.data?.membershipUuid}
            >
              Earn points
            </button>
          ) : null}
          <button
            type="button"
            className="btn ghost"
            onClick={() => setRedeemOpen(true)}
            disabled={!membershipQuery.data?.membershipUuid || (membershipQuery.data?.pointsBalance ?? 0) <= 0}
          >
            Redeem points
          </button>
          <button type="button" className="btn ghost" onClick={() => setAdjustmentOpen(true)}>Adjust points</button>
        </div>
      </section>

      <div className="membership-customer-grid">
        <MembershipCustomerSummaryCard membership={membershipQuery.data} />
        <PointsBalanceCard pointsBalance={membershipQuery.data?.pointsBalance || 0} />
      </div>

      <article className="appointments-v2-card">
        <div className="appointments-v2-card-header"><h3>Ledger filters</h3></div>
        <div className="appointments-v2-card-body">
          <div className="membership-customer-filter-chips">
            {EVENT_TYPES.map((type) => (
              <button
                key={type || '__all'}
                type="button"
                className={`membership-customer-filter-chip ${eventType === type ? 'is-active' : ''}`}
                onClick={() => setEventType(type)}
              >
                {EVENT_LABELS[type]}
              </button>
            ))}
          </div>
          <div className="membership-customer-filters">
            <input type="date" className="input" value={fromDate} min={minSelectableDate} onChange={(e) => setFromDate(e.target.value)} placeholder="From" />
            <input type="date" className="input" value={toDate} min={minSelectableDate} onChange={(e) => setToDate(e.target.value)} placeholder="To" />
            <button type="button" className="btn ghost small" onClick={() => { setEventType(''); setFromDate(''); setToDate(''); }}>
              Reset
            </button>
          </div>
          {(dateRangeSummary || entryCount > 0) ? (
            <p className="membership-customer-filter-summary">
              Showing {entryCount} {entryCount === 1 ? 'entry' : 'entries'}{dateRangeSummary ? ` ${dateRangeSummary}` : ''}
            </p>
          ) : null}
        </div>
      </article>

      <PointsLedgerTable
        rows={ledgerQuery.data?.items || []}
        loading={ledgerQuery.isLoading}
        page={page}
        hasNext={Boolean(ledgerQuery.data?.hasNext)}
        onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
        onNext={() => setPage((prev) => prev + 1)}
      />

      <ManualPointsAdjustmentDrawer
        open={adjustmentOpen}
        membershipUuid={membershipUuid}
        onClose={() => setAdjustmentOpen(false)}
      />
      <EarnPointsDrawer
        open={earnOpen}
        membershipUuid={membershipUuid}
        earnRatePaisePerPoint={membershipTypeQuery.data?.pointsEarnRatePaisePerPoint}
        onClose={() => setEarnOpen(false)}
      />
      <RedeemPointsDrawer
        open={redeemOpen}
        membershipUuid={membershipUuid}
        currentBalance={membershipQuery.data?.pointsBalance ?? 0}
        redeemPaisePerPoint={membershipTypeQuery.data?.pointsRedeemPaisePerPoint}
        minRedeem={membershipTypeQuery.data?.pointsMinRedeem}
        onClose={() => setRedeemOpen(false)}
      />
      <RevokeMembershipModal
        open={revokeOpen}
        membershipUuid={membershipQuery.data?.membershipUuid}
        onClose={() => setRevokeOpen(false)}
      />
      <RenewMembershipModal
        open={renewOpen}
        membershipUuid={membershipQuery.data?.membershipUuid}
        currentExpiresAt={membershipQuery.data?.expiresAt}
        onClose={() => setRenewOpen(false)}
      />
    </section>
  );
};

export default MembershipCustomerDetailPage;
